import React, { useEffect, useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connectInfiniteHits, connectStateResults } from 'react-instantsearch-dom';
import { Button, Icon, Loading, Typography } from 'ck-ui';
import { trackProductImpressions } from 'ck-utils/algoliaAnalytics';
import layout from 'ck-utils/layout.module.scss';
import { useWishlist } from 'ck-utils/hooks/useWishlist';
import { ProductListingPageContext, BundleLookPageContext } from '../../context';
import { ProgressBar } from '../../widgets';
import { ProductHit } from '../ProductHit';
import { ProductFeatureTile } from '../ProductHit/ProductFeatureTile';
import RESPONSIVE_COLUMNS from '../../data/responsiveColumns.json';
import styles from './ProductListContainer.module.scss';

const ProductHitList = ({
  listName,
  enableSeoThumbnails,
  isSearchStalled,
  searchResults,
  hits,
  hasPrevious,
  refinePrevious,
  hasMore,
  refineNext,
  syncPageUrl,
}) => {
  const { columns, isMobile, featureTile } = useContext(ProductListingPageContext);
  const { groups, selectedTag, onDeleteProduct, onAddProduct } = useContext(BundleLookPageContext) || {};
  const isByoBundle = !!groups;
  const responsiveColumns = RESPONSIVE_COLUMNS[`columns-${columns}`];
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const groupPosition = groups && groups.findIndex((group) => group.tag === selectedTag);
  const { nbHits, queryID, hitsPerPage, page, hits: sHits, index: indexName } = searchResults || {};
  const { wishListProductIds, wishListCollections } = useWishlist();

  /**
   * Do some Shopify Theme init after
   * 1. Breakpoint changes between mobile and desktop,
   * 2. Hits change (load more / filtering)
   */
  useEffect(() => {
    if (queryID && hits.length > 0) {
      window.theme.initCollectionAddToCart();
    }
  }, [isMobile, queryID, hits]);

  // Fire product impression tracking on when query ID changes
  useEffect(() => {
    if (queryID) {
      trackProductImpressions(sHits, hitsPerPage, page, listName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryID]);

  // Render Loading
  if (isSearchStalled && !nbHits) {
    return <Loading isLoading />;
  }

  // Render No results
  if (!nbHits) {
    return (
      <div>
        <Typography renderAs="h1" display="inline" variant="heading4">
          Sorry, we couldn't find any products
        </Typography>
      </div>
    );
  }

  const getByoCurrentSelection = (hit) => {
    const initial = { selectedVariantId: null, productPosition: 0 };
    if (!isByoBundle) return initial;

    const { products } = groups[groupPosition];
    return products.reduce((acc, product, productPosition) => {
      const { variantId } = product;
      if (!variantId) return acc;
      return hit.variants.reduce(
        (prev, variant) => (variant.id.indexOf(variantId) !== -1 ? { selectedVariantId: variantId, productPosition } : prev),
        acc
      );
    }, initial);
  };

  return (
    <>
      {enableSeoThumbnails && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: listName,
            itemListElement: hits
              .filter(hit => hit.image !== null)
              .slice(0, 3)
              .map((hit, hitIndex) => ({
                '@type': 'ListItem',
                position: hitIndex + 1,
                url: `${window.location.origin}/products/${hit.handle}`,
                image: hit.image,
                name: hit.title
              }))
          })}
        </script>
      )}
      {hasPrevious && (
        <section className={classnames('has-text-centered', layout.marginBottomLarge)}>
          <Button
            secondary
            size={isMobile ? null : 'large'}
            fullwidth="mobile"
            onClick={() => {
              // has to refine first to make sure the url ?page= is reverted
              refinePrevious();
              syncPageUrl(true);
            }}
            loading={isSearchStalled}
          >
            LOAD PREVIOUS
          </Button>
        </section>
      )}
      <div className="columns is-multiline is-mobile is-variable is-2-mobile">
        {hits.map((hit, hitIndex) => {
          const { selectedVariantId, productPosition } = getByoCurrentSelection(hit);

          return (
            <Fragment key={hit.objectID}>
              { featureTile?.position === hitIndex + 1 && (
                <ProductFeatureTile
                  key={`${hit.objectID}_featured`}
                  columns={columns}
                  isMobile={isMobile}
                  featureTile={featureTile}
                  position={hitIndex + 1}
                />
              )}
              <div className={classnames('column', responsiveColumns)}>
                <ProductHit
                  listName={listName}
                  indexName={indexName}
                  hit={hit}
                  isMobile={isMobile}
                  isBundle={isByoBundle}
                  canClickThrough={!isByoBundle}
                  showSash={!isByoBundle}
                  showAddToCartAtBottom={isByoBundle}
                  showQuickView={isByoBundle}
                  selectedVariantId={selectedVariantId}
                  columns={columns}
                  wishListCollections={wishListCollections}
                  addedToWishList={wishListProductIds?.includes(`${hit.id}`)}
                  wishlistHasLoaded={Boolean(wishListProductIds)}
                  onDeleteProduct={
                    onDeleteProduct
                      ? () => {
                        onDeleteProduct(groupPosition, productPosition);
                      }
                      : null
                  }
                  onAddProduct={
                    onAddProduct
                      ? (variantId) => onAddProduct(groupPosition, {
                        variantId,
                        imageUrl: hit.image,
                        vendor: hit.vendor,
                        title: hit.title,
                        shortTitle: hit.shortTitle,
                        price: hit.price,
                      })
                      : null
                  }
                />
              </div>
            </Fragment>
          );
        })}
      </div>
      <section className={layout.marginTopLarge}>
        <ProgressBar hits={hits} />
        {hasMore && (
          <div className={classnames(layout.marginTopLarge, styles.loadMoreContainer)}>
            <Button
              secondary
              size={isMobile ? null : 'large'}
              fullwidth="mobile"
              onClick={refineNext}
              loading={isSearchStalled}
            >
              LOAD MORE
            </Button>
            <Icon
              className={styles.scrollTop}
              size={isMobile ? 'large' : 'exLarge'}
              onClick={scrollTop}
              name="scroll-top"
            />
          </div>
        )}
      </section>
    </>
  );
};

ProductHitList.propTypes = {
  listName: PropTypes.string.isRequired,
  enableSeoThumbnails: PropTypes.bool,
  hits: PropTypes.arrayOf(PropTypes.object),
  searchResults: PropTypes.object,
  isSearchStalled: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  refineNext: PropTypes.func.isRequired,
  refinePrevious: PropTypes.func.isRequired,
  syncPageUrl: PropTypes.func.isRequired,
};

ProductHitList.defaultProps = {
  hits: [],
  searchResults: null,
  enableSeoThumbnails: false
};

const ProductListContainer = connectInfiniteHits(connectStateResults(ProductHitList));

export { ProductListContainer };
